const resolveApiBaseUrl = () => {
  const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
  if (!configuredBaseUrl) {
    return '';
  }

  if (typeof window === 'undefined') {
    return configuredBaseUrl;
  }

  const isLocalConfigured = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/i.test(configuredBaseUrl);
  const runningLocally = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  if (isLocalConfigured && !runningLocally) {
    return '';
  }

  return configuredBaseUrl;
};

const API_BASE_URL = resolveApiBaseUrl();

const buildUrl = (path) => {
  if (API_BASE_URL.endsWith('/')) {
    return `${API_BASE_URL.slice(0, -1)}${path}`;
  }
  return `${API_BASE_URL}${path}`;
};

const requestJson = async (path, options = {}) => {
  let response;
  try {
    response = await fetch(buildUrl(path), {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    });
  } catch (fetchError) {
    const error = new Error('Network error while contacting server');
    error.cause = fetchError;
    throw error;
  }

  const bodyText = await response.text();
  let data = {};
  if (bodyText) {
    try {
      data = JSON.parse(bodyText);
    } catch {
      const trimmed = bodyText.trim();
      const htmlLike = /^<!doctype html>|^<html[\s>]/i.test(trimmed);
      data = {
        error: htmlLike
          ? 'Server returned an unexpected HTML response'
          : `Unexpected server response (${response.status})`,
      };
    }
  }

  if (!response.ok) {
    const message = data?.error || 'Request failed';
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

export const sessionApi = {
  createGuestSession(payload = {}) {
    return requestJson('/api/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getGuestSession(code) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}`);
  },

  joinGuestSession(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  leaveGuestSession(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/leave`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  transferGuestSessionHost(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/host/transfer`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  endGuestSession(code, payload = {}) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/end`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  pingPresence(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/presence/ping`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  setLobbyLock(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/lobby`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getVirtualGameState(code) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/virtual-game`);
  },

  startVirtualGame(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/virtual-game/start`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateVirtualGameState(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/virtual-game/state`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  endVirtualGame(code, payload = {}) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/virtual-game/end`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getMessages(code, query = {}) {
    const params = new URLSearchParams();
    if (query.since) params.set('since', query.since);
    if (query.limit) params.set('limit', String(query.limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/messages${suffix}`);
  },

  sendMessage(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getVoiceSignals(code, query) {
    const params = new URLSearchParams();
    params.set('alias', query.alias);
    if (query.since) params.set('since', query.since);
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/voice/signals?${params.toString()}`);
  },

  sendVoiceSignal(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/voice/signal`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  startVirtualImposter(code, payload) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/imposter/start`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getVirtualImposterState(code) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/imposter/state`);
  },

  getVirtualImposterRole(code, alias) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/imposter/role?alias=${encodeURIComponent(alias)}`);
  },

  revealVirtualImposter(code) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/imposter/reveal`, {
      method: 'POST',
    });
  },

  endVirtualImposter(code) {
    return requestJson(`/api/sessions/${encodeURIComponent(code)}/imposter/end`, {
      method: 'POST',
    });
  },
};
